import { ky } from './ky';
import { ListResponse } from './common';

/**
 * 주문서 검증 문제 코드. 백엔드 `OrderPreviewProblemCode`와 매칭된다.
 */
export type OrderPreviewProblemCode =
  | 'INVALID_QUANTITY'
  | 'PRODUCT_NOT_FOUND'
  | 'PRODUCT_NOT_PURCHASABLE'
  | 'SALE_NOT_STARTED'
  | 'SALE_ENDED'
  | 'VARIANT_NOT_FOUND'
  | 'VARIANT_NOT_SELLABLE'
  | 'OUT_OF_STOCK'
  | 'NOT_SHIPPABLE_TO_COUNTRY'
  | 'SHIPPING_POLICY_NOT_FOUND';

export interface OrderLineCreateRequest {
  productId: number;
  variantId: number;
  quantity: number;
}

/**
 * 배송지 요청. 백엔드 `ShippingAddressRequest`와 매칭된다.
 * 필드명은 `AddressSchemaDto.fields`의 name과 일치하며 서버에서 국가별 주소 스키마로 검증된다.
 */
export interface ShippingAddressRequest {
  countryCode: string;
  recipientName: string;
  phoneNumber: string;
  postalCode: string;
  region: string;
  city: string;
  addressLine1: string;
  addressLine2?: string | null;
  customsClearanceId?: string | null;
}

/** 배송지 DTO. 백엔드 `ShippingAddressDto`와 매칭된다. */
export interface ShippingAddressDto {
  countryCode: string;
  recipientName: string;
  phoneNumber: string;
  postalCode: string;
  region: string;
  city: string;
  addressLine1: string;
  addressLine2?: string | null;
  customsClearanceId?: string | null;
}

/**
 * 주문서 미리보기 요청. 백엔드 `OrderPreviewRequest`와 매칭된다.
 * `shippingAddress`는 선택이며, 전달하면 서버가 주소 스키마로 검증한다. (아직 금액 산정에는 미사용)
 */
export interface OrderPreviewRequest {
  lines: OrderLineCreateRequest[];
  destinationCountryCode?: string | null;
  shippingAddress?: ShippingAddressRequest | null;
}

/**
 * 주문서 검증 문제. 백엔드 `OrderPreviewProblemDto`와 매칭된다.
 * 주문 단위 문제(SHIPPING_POLICY_NOT_FOUND 등)는 lineIndex/productId/variantId가 null 이다.
 */
export interface OrderPreviewProblemDto {
  lineIndex: number | null;
  productId: number | null;
  variantId: number | null;
  code: OrderPreviewProblemCode;
  message: string;
}

export interface OrderPreviewLineDto {
  lineIndex: number;
  productId: number;
  productTitle?: string | null;
  variantId: number;
  optionName?: string | null;
  optionValue?: string | null;
  skuCode?: string | null;
  quantity: number;
  unitPriceAmount?: number | null;
  currencyCode?: string | null;
  lineTotalAmount?: number | null;
  purchasable: boolean;
  problems: OrderPreviewProblemDto[];
}

/**
 * 주문서 미리보기 DTO. 백엔드 `OrderPreviewDto`와 매칭된다.
 * 가격/배송비/할인/리워드/세금/총액과 라인·전체 검증 문제를 포함한다.
 */
export interface OrderPreviewDto {
  purchasable: boolean;
  currencyCode?: string | null;
  subtotalAmount: number;
  shippingFeeAmount: number;
  discountAmount: number;
  rewardAmount: number;
  taxAmount: number;
  totalAmount: number;
  lines: OrderPreviewLineDto[];
  problems: OrderPreviewProblemDto[];
}

/**
 * 주문 생성 요청. 백엔드 `OrderCreateRequest`와 매칭된다.
 * `shippingAddress`는 필수이며 서버에서 국가별 주소 스키마로 검증된다. (누락/불일치 시 400)
 * `visitorKey`는 어트리뷰션/리워드 귀속용(비로그인 방문 식별)으로 선택 전달한다.
 */
export interface OrderCreateRequest {
  lines: OrderLineCreateRequest[];
  shippingAddress: ShippingAddressRequest;
  visitorKey?: string | null;
}

export interface OrderLineDto {
  id: number;
  productId: number;
  productTitleSnapshot: string;
  variantId: number;
  optionNameSnapshot: string;
  optionValueSnapshot: string;
  skuCodeSnapshot: string;
  quantity: number;
  unitPriceAmount: number;
  currencyCode: string;
  lineTotalAmount: number;
  noticeTemplateId?: number | null;
  noticeCategoryCode?: string | null;
  noticeCategoryName?: string | null;
  noticeTemplateVersion?: number | null;
  noticeSnapshotJson?: string | null;
}

/** 주문 상태. 백엔드 `OrderStatus` enum 과 매칭된다. */
export type OrderStatus =
  | 'CREATED'
  | 'PAID'
  | 'PREPARING'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'PURCHASE_CONFIRMED'
  | 'CANCELED';

/** 결제 상태. 백엔드 `PaymentStatus` enum 과 매칭된다. */
export type PaymentStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'FAILED'
  | 'CANCELED'
  | 'PARTIALLY_REFUNDED'
  | 'REFUNDED';

/** 배송 상태. 백엔드 `ShipmentStatus` enum 과 매칭된다. */
export type ShipmentStatus = 'PREPARING' | 'SHIPPED' | 'IN_TRANSIT' | 'DELIVERED' | 'PURCHASE_CONFIRMED';

/** 반품 요청 상태. 백엔드 `ReturnRequestStatus` enum 과 매칭된다. */
export type ReturnRequestStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELED';

/** 교환 요청 상태. 백엔드 `ExchangeRequestStatus` enum 과 매칭된다. */
export type ExchangeRequestStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELED';

/** 구매자 액션. 백엔드 `OrderBuyerAction` enum 과 매칭된다. */
export type OrderBuyerAction = 'CANCEL' | 'CONFIRM_RECEIPT' | 'REQUEST_RETURN' | 'REQUEST_EXCHANGE';

/** 타임라인 카테고리. 백엔드 `OrderTimelineCategory` enum 과 매칭된다. */
export type OrderTimelineCategory = 'ORDER' | 'PAYMENT' | 'SHIPMENT' | 'RETURN' | 'EXCHANGE' | 'REFUND';

/** 귀책 주체. 백엔드 `CommerceResponsibilityParty` enum 과 매칭된다. */
export type CommerceResponsibilityParty = 'BUYER' | 'SELLER' | 'COURIER' | 'PLATFORM';

/** 비용 부담 주체. 백엔드 `CommerceCostBearer` enum 과 매칭된다. */
export type CommerceCostBearer = 'BUYER' | 'SELLER' | 'PLATFORM';

/** 결제 요약. 백엔드 `OrderPaymentSummaryDto`와 매칭된다. */
export interface OrderPaymentSummaryDto {
  paymentId: number;
  status: PaymentStatus;
  amount: number;
  currencyCode: string;
  requestedAt: string;
  approvedAt?: string | null;
  failedAt?: string | null;
  canceledAt?: string | null;
}

/** 배송 요약. 백엔드 `OrderDeliverySummaryDto`와 매칭된다. */
export interface OrderDeliverySummaryDto {
  shipmentId: number;
  status: ShipmentStatus;
  courierName?: string | null;
  trackingNumber?: string | null;
  returnWindowDays: number;
  exchangeWindowDays: number;
  preparedAt: string;
  shippedAt?: string | null;
  inTransitAt?: string | null;
  deliveredAt?: string | null;
  purchaseConfirmedAt?: string | null;
}

/** 환불 요약. 백엔드 `OrderRefundSummaryDto`와 매칭된다. */
export interface OrderRefundSummaryDto {
  requestedAmount: number;
  completedAmount: number;
  currencyCode?: string | null;
  hasPendingRefund: boolean;
}

/** 구매자 액션 가용성. 백엔드 `OrderActionDto`와 매칭된다. */
export interface OrderActionDto {
  action: OrderBuyerAction;
  available: boolean;
  unavailableReasonCode?: string | null;
}

/** 취소/반품/교환 정책 기한. 백엔드 `OrderDeadlineDto`와 매칭된다. */
export interface OrderDeadlineDto {
  countryCode: string;
  cancellationWindowDays: number;
  returnWindowDays: number;
  exchangeWindowDays: number;
  cancelDeadline?: string | null;
  returnDeadline?: string | null;
  exchangeDeadline?: string | null;
}

/** 반품 요청. 백엔드 `ReturnRequestDto`와 매칭된다. */
export interface ReturnRequestDto {
  id: number;
  shipmentId: number;
  orderId: number;
  orderLineId: number;
  buyerMemberId: number;
  sellerAccountId: number;
  quantity: number;
  reason: string;
  responsibilityParty: CommerceResponsibilityParty;
  costBearer: CommerceCostBearer;
  status: ReturnRequestStatus;
  requestedAt: string;
  decidedAt?: string | null;
  decisionReason?: string | null;
  refundId?: number | null;
}

/** 교환 요청. 백엔드 `ExchangeRequestDto`와 매칭된다. */
export interface ExchangeRequestDto {
  id: number;
  shipmentId: number;
  orderId: number;
  orderLineId: number;
  buyerMemberId: number;
  sellerAccountId: number;
  quantity: number;
  reason: string;
  requestedOptionName: string;
  requestedOptionValue: string;
  responsibilityParty: CommerceResponsibilityParty;
  costBearer: CommerceCostBearer;
  status: ExchangeRequestStatus;
  requestedAt: string;
  decidedAt?: string | null;
  decisionReason?: string | null;
}

/**
 * 구매자 주문 상태 집계 DTO. 백엔드 `OrderStatusDto`와 매칭된다.
 * 주문/결제/배송/반품/교환/환불 상태와 가능한 액션·정책 기한을 함께 담는다.
 */
export interface OrderStatusDto {
  id: number;
  orderNumber: string;
  buyerMemberId: number;
  sellerAccountId: number;
  status: OrderStatus;
  totalAmount: number;
  currencyCode: string;
  createdAt?: string | null;
  lines: OrderLineDto[];
  shippingAddress?: ShippingAddressDto | null;
  payment?: OrderPaymentSummaryDto | null;
  delivery?: OrderDeliverySummaryDto | null;
  refund: OrderRefundSummaryDto;
  returns: ReturnRequestDto[];
  exchanges: ExchangeRequestDto[];
  actions: OrderActionDto[];
  deadlines: OrderDeadlineDto;
}

/** 타임라인 이벤트. 백엔드 `OrderTimelineEventDto`와 매칭된다. */
export interface OrderTimelineEventDto {
  category: OrderTimelineCategory;
  code: string;
  referenceId?: number | null;
  occurredAt: string;
}

/** 반품 요청 생성. 백엔드 `ReturnRequestCreateRequest`와 매칭된다. */
export interface ReturnRequestCreateRequest {
  orderLineId: number;
  quantity: number;
  reason: string;
  responsibilityParty: CommerceResponsibilityParty;
  costBearer: CommerceCostBearer;
}

/** 교환 요청 생성. 백엔드 `ExchangeRequestCreateRequest`와 매칭된다. */
export interface ExchangeRequestCreateRequest {
  orderLineId: number;
  quantity: number;
  reason: string;
  requestedOptionName: string;
  requestedOptionValue: string;
  responsibilityParty: CommerceResponsibilityParty;
  costBearer: CommerceCostBearer;
}

/**
 * 주문 DTO. 백엔드 `OrderDto`와 매칭된다.
 * 주문 생성 성공 시 상세로 이동하기 위한 식별자(id)를 담는다.
 * totalAmount는 소계+배송비+세금을 포함한 최종 금액이다.
 */
export interface OrderDto {
  id: number;
  orderNumber: string;
  buyerMemberId: number;
  sellerAccountId: number;
  status: string;
  subtotalAmount: number;
  shippingFeeAmount: number;
  taxAmount: number;
  totalAmount: number;
  currencyCode: string;
  createdAt?: string | null;
  lines: OrderLineDto[];
  shippingAddress?: ShippingAddressDto | null;
}

/**
 * 주문 API 클라이언트.
 * 백엔드 `OrderController`(/v1/orders)와 매칭된다. (lone-backend work_item 16)
 */
export default class OrderService {
  /** 주문서(가격/배송/세금/리워드/검증) 미리보기 */
  async preview(request: OrderPreviewRequest) {
    const response = await ky.post('v1/orders/preview', { json: request });
    return (await response.json()) as OrderPreviewDto;
  }

  /** 주문 생성 */
  async create(request: OrderCreateRequest) {
    const response = await ky.post('v1/orders', { json: request });
    return (await response.json()) as OrderDto;
  }

  /** 내 주문 목록(상태 집계) 조회 */
  async listMyOrders() {
    const response = await ky.get('v1/orders/me');
    return (await response.json()) as ListResponse<OrderStatusDto>;
  }

  /** 내 주문 상세(상태 집계) 조회 */
  async getMyOrder(orderId: number) {
    const response = await ky.get(`v1/orders/me/${orderId}`);
    return (await response.json()) as OrderStatusDto;
  }

  /** 내 주문 상태 타임라인 조회 */
  async getMyOrderTimeline(orderId: number) {
    const response = await ky.get(`v1/orders/me/${orderId}/timeline`);
    return (await response.json()) as ListResponse<OrderTimelineEventDto>;
  }

  /** 내 주문 취소 (결제 전 CREATED 상태만 가능) */
  async cancelMyOrder(orderId: number) {
    const response = await ky.post(`v1/orders/me/${orderId}/cancel`);
    return (await response.json()) as OrderStatusDto;
  }

  /** 구매 확정 (배송 완료 상태만 가능) */
  async confirmReceipt(orderId: number) {
    const response = await ky.post(`v1/orders/me/${orderId}/confirm-receipt`);
    return (await response.json()) as OrderStatusDto;
  }

  /** 반품 요청 */
  async requestReturn(orderId: number, request: ReturnRequestCreateRequest) {
    const response = await ky.post(`v1/orders/me/${orderId}/return-requests`, { json: request });
    return (await response.json()) as ReturnRequestDto;
  }

  /** 교환 요청 */
  async requestExchange(orderId: number, request: ExchangeRequestCreateRequest) {
    const response = await ky.post(`v1/orders/me/${orderId}/exchange-requests`, { json: request });
    return (await response.json()) as ExchangeRequestDto;
  }
}
